import {
  FeatureFlag,
  type FeatureFlagType,
  PermissionSlug,
} from './featureFlags';
import { groupSections, type SectionGroup } from './groupSections';

/**
 * One entry per section of the Admin tab.
 *
 * The tab itself has no permission of its own: it appears when the user can see
 * at least one section, which is what {@link visibleAdminSections} answers. A single
 * master slug meant a granular permission was useless without it, so granting
 * one and forgetting the other silently hid the section.
 */
export interface AdminSectionDefinition {
  /** Stable id, used as the tab value and the `?section=` query parameter. */
  id: string;
  label: string;
  viewSlug: string;
  editSlug: string;
  /** Other slugs that also open the section, for a part of it they gate. */
  alsoOpenedBy?: string[];
  /** Sections that only exist where the EMR flows do. */
  rogerEmrOnly: boolean;
  /**
   * A WorkOS org flag the section is hidden behind: while it is in progress,
   * or because it configures a feature that is off for the agency without it.
   * Omitted for a section that is simply live.
   */
  featureFlag?: FeatureFlagType;
  /** The nav heading the section is listed under. */
  group: AdminSectionGroupId;
  /** Other words someone might search the settings list for. */
  keywords?: string[];
  /** Also shown to any Roger employee, who may hold no agency role. */
  openedByRogerTeam?: boolean;
}

export type AdminSectionGroupId =
  | 'organization'
  | 'fax'
  | 'staff'
  | 'visits'
  | 'templates'
  | 'billing'
  | 'reporting';

export const ADMIN_SECTION_GROUP_LABELS: Record<AdminSectionGroupId, string> = {
  organization: 'Organization',
  fax: 'Fax',
  staff: 'Staff',
  visits: 'Visits',
  templates: 'Templates',
  billing: 'Billing',
  reporting: 'Reporting',
};

export const ADMIN_SECTIONS: readonly AdminSectionDefinition[] = [
  {
    // Shared by intake and EMR agencies. Tab is "Agency" so more
    // agency-level settings can land here later; print name is the first field.
    id: 'agency-print-name',
    label: 'Agency',
    viewSlug: PermissionSlug.AGENCY_PRINT_NAME_VIEW,
    editSlug: PermissionSlug.AGENCY_PRINT_NAME_EDIT,
    rogerEmrOnly: false,
    group: 'organization',
    keywords: ['print name', 'agency name'],
  },
  {
    id: 'clinic-info',
    label: 'Clinics',
    viewSlug: PermissionSlug.CLINIC_INFO_VIEW,
    editSlug: PermissionSlug.CLINIC_INFO_EDIT,
    rogerEmrOnly: false,
    group: 'organization',
    keywords: ['branches', 'locations', 'address', 'NPI'],
  },
  {
    // Moving the agency's existing fax numbers to Roger. Not EMR-only: an
    // intake-only agency receives referrals by fax too. Submitting the port
    // is a separate slug the section checks for itself.
    id: 'fax-number-porting',
    label: 'Fax number porting',
    viewSlug: PermissionSlug.FAX_NUMBER_PORTS_EDIT,
    editSlug: PermissionSlug.FAX_NUMBER_PORTS_EDIT,
    rogerEmrOnly: false,
    group: 'fax',
    keywords: ['fax', 'port', 'phone number', 'transfer'],
  },
  {
    // Where each fax type is filed in the chart, so Roger EMR only.
    id: 'fax-document-routing',
    label: 'Document routing',
    viewSlug: PermissionSlug.FAX_ROUTING_EDIT,
    editSlug: PermissionSlug.FAX_ROUTING_EDIT,
    rogerEmrOnly: true,
    group: 'fax',
    keywords: ['fax', 'routing', 'filing', 'document types', 'default task'],
  },
  {
    // Moving an agency onto Roger: the go-live date and the External EMR
    // settings, which were chart policy first, so the chart-workflow slugs.
    id: 'onboarding',
    label: 'Onboarding',
    viewSlug: PermissionSlug.CHART_WORKFLOW_VIEW,
    editSlug: PermissionSlug.CHART_WORKFLOW_EDIT,
    rogerEmrOnly: true,
    group: 'organization',
    openedByRogerTeam: true,
    keywords: [
      'go-live',
      'External EMR',
      'Kinnser',
      'WellSky',
      'Take Over',
      'last day',
      'billing lag',
      'chart lock',
    ],
  },
  {
    id: 'patient-tags',
    label: 'Patient Tags',
    viewSlug: PermissionSlug.EMR_MANAGE,
    editSlug: PermissionSlug.EMR_MANAGE,
    rogerEmrOnly: true,
    group: 'organization',
    keywords: ['tags', 'patient tags', 'labels'],
  },
  {
    id: 'physicians',
    label: 'Physicians',
    viewSlug: PermissionSlug.CLINIC_PHYSICIANS_VIEW,
    editSlug: PermissionSlug.CLINIC_PHYSICIANS_EDIT,
    rogerEmrOnly: false,
    group: 'staff',
    keywords: ['doctors', 'NPI', 'ordering physician'],
  },
  {
    // Staff credential compliance is not an EMR-only concern, and User Manager
    // is shared with intake-only agencies, so this one is not rogerEmrOnly.
    // The catalog has no separate view slug: the screen is the edit screen.
    id: 'user-license-types',
    label: 'Licenses',
    viewSlug: PermissionSlug.USER_LICENSE_TYPES_EDIT,
    editSlug: PermissionSlug.USER_LICENSE_TYPES_EDIT,
    rogerEmrOnly: false,
    group: 'staff',
    keywords: ['licenses', 'credentials', 'compliance'],
  },
  {
    id: 'referral-sources',
    label: 'Referral Sources',
    viewSlug: PermissionSlug.REFERRAL_SOURCES_VIEW,
    editSlug: PermissionSlug.REFERRAL_SOURCES_EDIT,
    rogerEmrOnly: true,
    group: 'staff',
    keywords: ['referrals', 'marketing'],
  },
  {
    // Every task type's aliases, billing and Create on admission column (the
    // old Patient Creation section). EMR-only: an intake-only agency never
    // schedules visits.
    id: 'task-types',
    label: 'Visit Types',
    viewSlug: PermissionSlug.TASK_TYPES_VIEW,
    editSlug: PermissionSlug.TASK_TYPES_EDIT,
    alsoOpenedBy: [
      PermissionSlug.PATIENT_CREATION_SETTINGS_VIEW,
      PermissionSlug.PATIENT_CREATION_SETTINGS_EDIT,
    ],
    rogerEmrOnly: true,
    group: 'visits',
    keywords: [
      'aliases',
      'billable',
      'non-billable',
      'orders',
      'patient creation',
      'admission',
      'auto-create',
    ],
  },
  {
    id: 'scheduling-points',
    label: 'Scheduling Points',
    viewSlug: PermissionSlug.SCHEDULING_POINTS_VIEW_CONFIG,
    editSlug: PermissionSlug.SCHEDULING_POINTS_EDIT_CONFIG,
    rogerEmrOnly: true,
    group: 'visits',
    keywords: ['points', 'productivity'],
  },
  {
    id: 'chart-workflow',
    label: 'Chart Workflow',
    viewSlug: PermissionSlug.CHART_WORKFLOW_VIEW,
    editSlug: PermissionSlug.CHART_WORKFLOW_EDIT,
    rogerEmrOnly: true,
    group: 'visits',
    keywords: ['signature', 'signing', 'OASIS', 'scrubber', 'QA', 'export'],
  },
  {
    // Standardized text an agency loads into visit-form text boxes, and below
    // it, into physician orders. Both are EMR-only because visit forms and
    // physician orders are EMR records, which an intake-only agency has none of.
    id: 'form-templates',
    label: 'Text Templates',
    viewSlug: PermissionSlug.FORM_TEMPLATES_VIEW,
    editSlug: PermissionSlug.FORM_TEMPLATES_EDIT,
    rogerEmrOnly: true,
    group: 'templates',
    keywords: ['templates', 'snippets'],
  },
  {
    id: 'physician-order-templates',
    label: 'Physician Order Templates',
    viewSlug: PermissionSlug.FORM_TEMPLATES_VIEW,
    editSlug: PermissionSlug.FORM_TEMPLATES_EDIT,
    rogerEmrOnly: true,
    group: 'templates',
    keywords: ['orders', 'physician order', 'Kinnser', 'use template'],
  },
  {
    id: 'insurance',
    label: 'Insurance',
    viewSlug: PermissionSlug.AGENCY_INSURANCE_VIEW,
    editSlug: PermissionSlug.AGENCY_INSURANCE_EDIT,
    rogerEmrOnly: true,
    group: 'billing',
    keywords: ['payers', 'payors', 'billing rates', 'rates', 'enrollment'],
  },
  {
    id: 'hhrg-costs',
    label: 'HHRG Costs',
    viewSlug: PermissionSlug.HHRG_COSTS_VIEW,
    editSlug: PermissionSlug.HHRG_COSTS_EDIT,
    rogerEmrOnly: true,
    group: 'billing',
    keywords: ['HHRG', 'staff cost', 'cost per visit', 'worksheet'],
  },
  {
    // Which visit statuses reach Payroll Manager. Behind Payroll Manager's own
    // flag: without it there is nothing for the setting to change, and the
    // settings API refuses the agency.
    id: 'payroll',
    label: 'Payroll',
    viewSlug: PermissionSlug.PAYROLL_SETTINGS_VIEW,
    editSlug: PermissionSlug.PAYROLL_SETTINGS_EDIT,
    rogerEmrOnly: true,
    featureFlag: FeatureFlag.PAYROLL_MANAGER,
    group: 'billing',
    keywords: ['eligible statuses', 'unpaid', 'paid'],
  },
  {
    // The monthly HHCAHPS sample file. Kinnser keeps it under Reports &
    // Administration; it is a monthly chore, not a daily workspace.
    id: 'cahps',
    label: 'CAHPS',
    viewSlug: PermissionSlug.CAHPS_MANAGER,
    editSlug: PermissionSlug.CAHPS_MANAGER,
    rogerEmrOnly: true,
    group: 'reporting',
    keywords: ['HHCAHPS', 'survey', 'sample', 'SHP', 'Fazzi', 'export'],
  },
];

/**
 * The sections this user actually gets, in nav order.
 *
 * The one answer to "is there an Admin tab for this person" — the sidebar link,
 * the page's own gate and the tab strip all call this so they cannot disagree.
 * They did once: the gate counted `rogerEmrOnly` sections that the tab strip
 * then filtered out, so a user at an agency without the EMR product who held
 * only EMR-only slugs was let in to a page with no tabs on it.
 */
export function visibleAdminSections(
  hasAnyPermission: (slugs: string[]) => boolean,
  isRogerEmrEnabled: boolean,
  hasFeatureFlag: (featureFlag: FeatureFlagType) => boolean,
  isRogerEmployee = false
): AdminSectionDefinition[] {
  return ADMIN_SECTIONS.filter(section => {
    if (section.rogerEmrOnly && !isRogerEmrEnabled) return false;
    // Required, not optional with a default: the sidebar link, the landing-path
    // gate and the tab strip all call this, and a caller that silently skipped
    // the flag would reopen the disagreement described above.
    if (section.featureFlag && !hasFeatureFlag(section.featureFlag))
      return false;
    if (section.openedByRogerTeam && isRogerEmployee) return true;

    return hasAnyPermission([
      section.viewSlug,
      section.editSlug,
      ...(section.alsoOpenedBy ?? []),
    ]);
  });
}

export type AdminNavEntry = SectionGroup<AdminSectionDefinition>;

/** The visible sections under their nav headings, in nav order. */
export function adminNavEntries(
  sections: AdminSectionDefinition[]
): AdminNavEntry[] {
  return groupSections(sections, ADMIN_SECTION_GROUP_LABELS);
}
